import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActiveStudentId, getActiveSchoolId } from '../utils/activeSchool';
import { syncPendingPayments } from '../utils/paymentSync';

export default function SessionInventoryPanel({ session, api, onClose, onSuccess }) {
    const { t } = useTranslation(['rentalPage', 'common']);
    const studentId = getActiveStudentId() || session?.studentId;
    const [cart, setCart] = useState({});
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState('');

    const lines = useMemo(() => Object.entries(cart).map(([id, qty]) => ({
        inventoryItemId: parseInt(id, 10),
        quantity: qty
    })), [cart]);

    useEffect(() => {
        if (!session || !studentId) return;
        loadPreview([]);
    }, [session?.groupId, session?.sessionDate, studentId]);

    useEffect(() => {
        if (!session || !studentId) return;
        const t = setTimeout(() => loadPreview(lines), 300);
        return () => clearTimeout(t);
    }, [cart]);

    async function loadPreview(inventoryLines) {
        setLoading(true);
        setError('');
        try {
            const res = await api.post('/planned-attendance/session-payment-preview', {
                studentId,
                groupId: session.groupId,
                slotId: session.slotId,
                sessionDate: session.sessionDate,
                inventoryLines: inventoryLines
            });
            setPreview(res.data);
        } catch (err) {
            setError(err.response?.data?.message || t('rentalPage:loadError'));
        }
        setLoading(false);
    }

    function toggleItem(id) {
        setCart(prev => {
            const next = { ...prev };
            if (next[id]) delete next[id];
            else next[id] = 1;
            return next;
        });
    }

    function setQty(id, q) {
        setCart(prev => ({ ...prev, [id]: Math.max(1, parseInt(q, 10) || 1) }));
    }

    async function checkout(payMethod) {
        if (lines.length === 0) {
            setError(t('rentalPage:pickInventory'));
            return;
        }
        setPaying(true);
        setError('');
        try {
            const res = await api.post('/planned-attendance/book-session', {
                studentId,
                groupId: session.groupId,
                slotId: session.slotId,
                sessionDate: session.sessionDate,
                inventoryLines: lines,
                payMethod
            });
            if (res.data.confirmationUrl) {
                window.open(res.data.confirmationUrl, '_blank');
                const schoolId = getActiveSchoolId() || preview?.schoolId;
                alert(t('rentalPage:payTabHint'));
                setTimeout(async () => {
                    await syncPendingPayments(api, schoolId);
                    onSuccess?.();
                }, 5000);
            } else {
                alert(res.data.message || t('rentalPage:rentSuccess'));
            }
            onSuccess?.();
            onClose();
        } catch (err) {
            const d = err.response?.data;
            if (d?.error === 'Insufficient balance') {
                setError(t('rentalPage:insufficientBalance', { required: d.required, balance: d.currentBalance }));
            } else {
                setError(d?.message || t('rentalPage:checkoutError'));
            }
        }
        setPaying(false);
    }

    const items = preview?.inventoryItems || [];
    const invTotal = preview?.inventoryTotal ?? 0;

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px'
        }} onClick={onClose}>
            <div
                style={{
                    background: '#fff', borderRadius: '14px', maxWidth: '520px', width: '100%',
                    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: 0, fontSize: '17px' }}>{t('rentalPage:sessionTitle')}</h3>
                    <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
                        {session?.sessionDate} · {session?.groupName}
                        {session?.startTime && ` · ${session.startTime}–${session.endTime}`}
                    </p>
                    {preview?.lessonStatusText && (
                        <p style={{
                            margin: '8px 0 0', fontSize: '12px', padding: '8px 10px', borderRadius: '8px',
                            background: preview.lessonCoveredBySubscription ? '#f0fdf4' : '#f8fafc',
                            color: '#334155'
                        }}>
                            {preview.lessonStatusText}
                        </p>
                    )}
                </div>

                <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
                    {loading && !items.length ? (
                        <p style={{ color: '#64748b' }}>{t('common:loading')}</p>
                    ) : items.length === 0 ? (
                        <p style={{ color: '#888' }}>{t('rentalPage:emptyCatalog')}</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {items.map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        padding: '12px', borderRadius: '10px',
                                        border: cart[item.id] ? '2px solid #EB3333' : '1px solid #e5e7eb',
                                        cursor: item.availableQuantity > 0 ? 'pointer' : 'not-allowed',
                                        opacity: item.availableQuantity > 0 ? 1 : 0.5
                                    }}
                                    onClick={() => item.availableQuantity > 0 && toggleItem(item.id)}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                        <strong style={{ fontSize: '14px' }}>{item.name}</strong>
                                        <span style={{ fontWeight: 700, color: '#EB3333' }}>{item.price} ₽</span>
                                    </div>
                                    {item.characteristics && (
                                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{item.characteristics}</div>
                                    )}
                                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                        {t('rentalPage:inStock', { qty: item.availableQuantity })}
                                    </div>
                                    {cart[item.id] && (
                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                                            <label style={{ fontSize: '12px' }}>{t('rentalPage:quantity')}</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={item.availableQuantity}
                                                value={cart[item.id]}
                                                onChange={e => setQty(item.id, e.target.value)}
                                                style={{ width: '56px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #ddd' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {error && <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '10px' }}>{error}</p>}
                </div>

                <div style={{ padding: '14px 18px', borderTop: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                        <span>{t('rentalPage:rentSessionLine')}</span>
                        <strong>{invTotal.toFixed(2)} ₽</strong>
                    </div>
                    {preview?.lessonAmountDue > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', color: '#64748b' }}>
                            <span>{t('rentalPage:lessonLine')}</span>
                            <span>{preview.lessonAmountDue.toFixed(2)} ₽</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            disabled={paying || lines.length === 0}
                            onClick={() => checkout('balance')}
                            style={{
                                flex: 1, minWidth: '120px', padding: '10px', borderRadius: '10px', border: 'none',
                                background: '#EB3333', color: '#fff', fontWeight: 600, cursor: 'pointer',
                                opacity: paying || lines.length === 0 ? 0.6 : 1
                            }}
                        >
                            {paying ? '…' : t('rentalPage:payBalance')}
                        </button>
                        <button
                            type="button"
                            disabled={paying || lines.length === 0}
                            onClick={() => checkout('card')}
                            style={{
                                flex: 1, minWidth: '120px', padding: '10px', borderRadius: '10px', border: '1px solid #EB3333',
                                background: '#fff', color: '#EB3333', fontWeight: 600, cursor: 'pointer',
                                opacity: paying || lines.length === 0 ? 0.6 : 1
                            }}
                        >
                            {t('rentalPage:payCardShort')}
                        </button>
                        <button type="button" onClick={onClose} style={{
                            padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1',
                            background: '#fff', cursor: 'pointer'
                        }}>
                            {t('common:cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
